import { createServiceRequest } from '@commenda/api';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import { INDIRECT_TAX_UPLOAD_SERVICE_ID } from '@commenda/types';
import { useState } from 'react';
import { TSetNotification } from '@commenda/types/src/random';
import { useRouter } from 'next/navigation';

import { RadioCardKS } from '../../../components';

const OPTION_COMMENDA_IMPORT = 'commenda_import';
const OPTION_IMPORT_MYSELF = 'import_myself';

type ImportOption = typeof OPTION_COMMENDA_IMPORT | typeof OPTION_IMPORT_MYSELF;

interface ImportTransactionsChoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  corporationId: number;
  setNotification: TSetNotification;
  onSelectImportMyself: () => void;
}

export const ImportTransactionsChoiceModal = ({
  open,
  onOpenChange,
  companyId,
  corporationId,
  setNotification,
  onSelectImportMyself,
}: ImportTransactionsChoiceModalProps) => {
  const router = useRouter();
  const [selectedOption, setSelectedOption] =
    useState<ImportOption>(OPTION_COMMENDA_IMPORT);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (selectedOption === OPTION_IMPORT_MYSELF) {
      onSelectImportMyself();
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createServiceRequest({
        serviceCompositeId: INDIRECT_TAX_UPLOAD_SERVICE_ID,
        companyId,
        corporationId,
      });

      if (!response.success) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Failed to create import request',
        });
        setIsSubmitting(false);
        return;
      }

      if (response.data?.id) router.push(`/service-request/${response.data.id}`);
      else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'An error occurred while creating the import request',
        });
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating import service request:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'An error occurred while creating the import request',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[676px]">
        <DialogHeader className="border-b-0">
          <DialogTitle>Import transactions</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-6 pb-6">
          <RadioCardKS
            heading={
              <div className="flex items-center gap-2">
                <span className="txt-small-bold text-text-primary">Let Commenda import my transactions</span>
                <Badge variant="blue" size="sm">
                  Recommended
                </Badge>
              </div>
            }
            description="Upload your file — our team will import your transactions within 2 business days."
            onClick={() => setSelectedOption(OPTION_COMMENDA_IMPORT)}
            isActive={selectedOption === OPTION_COMMENDA_IMPORT}
          />
          <RadioCardKS
            heading="Import transactions myself"
            description="Upload your file and match the columns in your file to Commenda's transaction template."
            onClick={() => setSelectedOption(OPTION_IMPORT_MYSELF)}
            isActive={selectedOption === OPTION_IMPORT_MYSELF}
          />
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleContinue}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
